import { createSlice } from '@reduxjs/toolkit';
import authActions from 'features/auth/services/actions';
import settingServiceActions from 'features/settingService/services/actions';
import { set } from 'lodash';
import { persistReducer } from 'redux-persist';
import createFilter from 'redux-persist-transform-filter';
import storagePersist from 'redux-persist/lib/storage';
import { EHideFieldReport } from 'utils/hide-field-report';
import actions from './actions';
import { COMBO_SERVICES_DATA, HomeLayoutCode, NAME_REDUCER, ROLE_CODE_PERMISSION, SERVICE_COLOR_SETS } from './constants';
import { IScheduleBookingResData } from './types/booking';
import { ICustomerItemData } from './types/customer';
import { IShopState } from './types/reducer';
import { IStaffItemData } from './types/staff';

const initialState: IShopState = {
  categories: [],
  productCategories: [],
  productsVIP: [],
  packagePrograms: [],
  staffs: [],
  staffsAvailable: [],
  anybody: null,
  customers: [],
  recentCustomers: [],
  shopInfo: null,
  specialDiscounts: [],
  coupons: [],
  discountSetting: null,
  orderPaymentMethods: [],
  allSetting: null,
  totalStation: 0,
  scheduleBooking: null,
  couponPrint: null,
  feeCreditCard: null,
  taxConfig: null,
  productTaxConfig: null,
  checkInOnlineLink: null,
  simpleMenu: [],
  simpleMenuConfigCheckIn: false,
  optionTips: [],
  optionTipsAll: [],
  quickBookingService: [],
  versionUI: 'V2',
  staffsAvailableBooking: [],
  printCheckSetting: null,
  settingReferral: [],
  loyaltyRedeemConfig: null,
  hideFieldReport: [],
  agreementData: null,
  cleaningFee: null,
  permissionConfig: [],
  cleaningFeeV2: null,
};

export const Slice = createSlice({
  name: NAME_REDUCER,
  initialState,
  reducers: {
    setVersionUI: (state, { payload }: { payload: string }) => {
      state.versionUI = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(actions.changeHomeLayout, (state, { payload }) => {
        set(state.allSetting || {}, 'layoutHomeCode', payload);
      })
      .addCase(actions.get.permissionConfig.success, (state, { payload }) => {
        state.permissionConfig = payload;
        payload.forEach(mod => {
          mod.features.forEach(feature => {
            const key = mod.moduleCode + '/' + feature.featureCode;
            set(ROLE_CODE_PERMISSION, key, feature.requirePin);
          });
        });
      })
      .addCase(actions.initShopData, (state) => {
        set(state.allSetting || {}, 'versionUI', 'V2');
        state.versionUI = 'V2';
      })
      .addCase(actions.get.cleaningFee.success, (state, { payload }) => {
        state.cleaningFee = payload;
      })
      .addCase(actions.get.cleaningFeeV2.success, (state, { payload }) => {
        state.cleaningFeeV2 = payload;
      })
      .addCase(actions.get.agreementConsentForm.success, (state, { payload }) => {
        state.agreementData = payload;
      })
      .addCase(settingServiceActions.getLoyaltyReward.success, (state, { payload }) => {
        state.loyaltyRedeemConfig = payload.find(o => o.type === 'LOYALTY_REDEMPTION') || null;
      })
      .addCase(actions.get.settingReferral.success, (state, { payload }) => {
        state.settingReferral = payload;
      })
      .addCase(actions.get.printCheckSetting.success, (state, { payload }) => {
        state.printCheckSetting = payload;
      })
      .addCase(actions.get.staffAvailableBooking.success, (state, { payload }) => {
        state.staffsAvailableBooking = payload;
      })
      .addCase(actions.get.quickBookingService.success, (state, { payload }) => {
        state.quickBookingService = payload;
      })
      .addCase(actions.get.optionTipsAll.success, (state, { payload }) => {
        state.optionTipsAll = payload;
      })
      .addCase(actions.get.optionTips.success, (state, { payload }) => {
        state.optionTips = payload;
      })
      .addCase(actions.get.simpleMenuConfigCheckIn.success, (state, { payload }) => {
        state.simpleMenuConfigCheckIn = payload;
      })
      .addCase(actions.get.simpleMenu.success, (state, { payload }) => {
        state.simpleMenu = payload;
        state.simpleMenu.map(cate => cate.services.map(o => {
          if (o.backgroundColor) {
            set(SERVICE_COLOR_SETS, o.id, o.backgroundColor);
          }
        }));
      })
      .addCase(actions.get.scheduleBooking.success, (state, { payload }) => {
        const data = payload as IScheduleBookingResData;

        state.scheduleBooking = {
          ['Sun']: {
            enable: data.enableSun,
            endTime: data.sunEndTime,
            startTime: data.sunStartTime,
          },
          ['Sat']: {
            enable: data.enableSat,
            endTime: data.satEndTime,
            startTime: data.satStartTime,
          },
          ['Fri']: {
            enable: data.enableFri,
            endTime: data.friEndTime,
            startTime: data.friStartTime,
          },
          ['Thu']: {
            enable: data.enableThu,
            endTime: data.thuEndTime,
            startTime: data.thuStartTime,
          },
          ['Wed']: {
            enable: data.enableWed,
            endTime: data.wedEndTime,
            startTime: data.wedStartTime,
          },
          ['Tue']: {
            enable: data.enableTue,
            endTime: data.tueEndTime,
            startTime: data.tueStartTime,
          },
          ['Mon']: {
            enable: data.enableMon,
            endTime: data.monEndTime,
            startTime: data.monStartTime,
          },
        };
      })
      .addCase(actions.get.checkInOnlineLink.success, (state, { payload }) => {
        state.checkInOnlineLink = payload;
      })
      .addCase(actions.get.taxConfig.success, (state, { payload }) => {
        state.taxConfig = payload;
      })
      .addCase(actions.get.productTaxConfig.success, (state, { payload }) => {
        state.productTaxConfig = payload;
      })
      .addCase(actions.get.feeCreditCard.success, (state, { payload }) => {
        state.feeCreditCard = payload;
      })
      .addCase(actions.get.couponPrint.success, (state, { payload }) => {
        state.couponPrint = payload;
      })
      .addCase(actions.resetCouponBillPrint, (state) => {
        state.couponPrint = null;
      })
      .addCase(actions.get.recentCustomers.success, (state, { payload }) => {
        state.recentCustomers = payload;
      })
      .addCase(actions.get.totalStation.success, (state, { payload }) => {
        state.totalStation = payload;
      })
      .addCase(actions.get.productsVIP.success, (state, { payload }) => {
        state.productsVIP = payload;
      })
      .addCase(actions.get.packagePrograms.success, (state, { payload }) => {
        state.packagePrograms = payload;
      })
      .addCase(actions.get.productCategories.success, (state, { payload }) => {
        state.productCategories = payload;
      })
      .addCase(actions.get.updateProduct, (state, { payload }) => {
        state.productCategories = state.productCategories.map((item) => {
          if (item.id === payload.catId) {
            return {
              ...item,
              products: item.products.map((product) => product.id === payload.id ? payload : product),
            };
          }
          return item;
        });
      })
      .addCase(actions.get.createProduct, (state, { payload }) => {
        state.productCategories = state.productCategories.map((item) => {
          if (item.id === payload.catId) {
            return {
              ...item,
              products: [...item.products, payload],
            };
          }
          return item;
        });
      })
      .addCase(actions.get.allSetting.success, (state, { payload }) => {
        state.allSetting = payload;
        state.allSetting.versionUI = 'V2';
        state.hideFieldReport = payload.hideFieldReport?.split(',') as EHideFieldReport[];
        if (!state.allSetting?.layoutHomeCode) {
          set(state.allSetting, 'layoutHomeCode', HomeLayoutCode.LAYOUT_1);
        }
      })
      .addCase(actions.get.otherPaymentMethod.success, (state, { payload }) => {
        state.orderPaymentMethods = payload;
      })
      .addCase(actions.get.discountSetting.success, (state, { payload }) => {
        state.discountSetting = payload;
      })
      .addCase(actions.get.coupons.success, (state, { payload }) => {
        state.coupons = payload;
      })
      .addCase(actions.get.specialDiscount.success, (state, { payload }) => {
        state.specialDiscounts = payload;
      })
      .addCase(actions.get.shopInfo.success, (state, { payload }) => {
        state.shopInfo = payload;
      })
      .addCase(actions.get.staffsAvailable.success, (state, { payload }) => {
        state.staffsAvailable = payload;
      })
      .addCase(actions.get.categories.fetch, (state) => {
        state.categories.map(cate => cate.services.map(o => {
          if (o.backgroundColor) {
            set(SERVICE_COLOR_SETS, o.id, o.backgroundColor);
          }
          if (o.childServices?.length) {
            set(COMBO_SERVICES_DATA, o.id, { ...o });
          }
        }));
      })
      .addCase(actions.get.categories.success, (state, { payload }) => {
        state.categories = payload;
        state.categories.map(cate => cate.services.map(o => {
          if (o.backgroundColor) {
            set(SERVICE_COLOR_SETS, o.id, o.backgroundColor);
          }
          if (o.childServices?.length) {
            set(COMBO_SERVICES_DATA, o.id, { ...o });
          }
        }));
      })
      .addCase(actions.get.staffs.success, (state, { payload }) => {
        state.staffs = payload;
        state.anybody = payload?.find((o: IStaffItemData) => o.anyBody) || null;
      })
      .addCase(actions.get.customers.success, (state, { payload }) => {
        state.customers = (payload as ICustomerItemData[]).filter(o => !!o.phone);
      })
      .addCase(authActions.ownerLogout, (state) => {
        state.totalStation = 0;
        state.categories = [];
        state.productCategories = [];
        state.productsVIP = [];
        state.packagePrograms = [];
        state.staffs = [];
        state.staffsAvailable = [];
        state.customers = [];
        state.shopInfo = null;
        state.specialDiscounts = [];
        state.coupons = [];
        state.discountSetting = null;
        state.orderPaymentMethods = [];
        state.allSetting = null;
        state.totalStation = 0;
      })
      .addCase(actions.deleteProducts, (state, { payload }) => {
        state.productCategories = state.productCategories.map((item) => {
          if (item.id === payload.categoryId) {
            return {
              ...item,
              products: item.products.filter((product) => !payload.ids.includes(product.id)),
            };
          }
          return item;
        });
      })
      .addCase(actions.deleteProductById, (state, { payload }) => {
        state.productCategories = state.productCategories.map((item) => {
          if (item.id === payload.categoryId) {
            return {
              ...item,
              products: item.products.filter((product) => product.id !== payload.id),
            };
          }
          return item;
        });
      });
  },
});

export const shopUIActions = Slice.actions;

const shopFilter = createFilter(
  'shop',
  ['permissionConfig', 'allSetting', 'categories']
);

export const shopPersistConfig = {
  key: 'shop',
  storage: storagePersist,
  whitelist: ['permissionConfig', 'allSetting', 'categories'],
  transforms: [shopFilter],
};

const shopServiceReducer = persistReducer<IShopState>(shopPersistConfig, Slice.reducer);

export default shopServiceReducer;
